import React, { useState, useEffect } from 'react';
import * as Sentry from "@sentry/react";
import Dropdown from 'react-bootstrap/Dropdown';
import DropdownButton from 'react-bootstrap/DropdownButton';
import Button from 'react-bootstrap/Button';
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Collapse from 'react-bootstrap/Collapse';
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Slider from '@material-ui/core/Slider';
import Checkbox from '@material-ui/core/Checkbox';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CheckBoxSharpIcon from '@material-ui/icons/CheckBoxSharp';
import CheckBoxOutlineBlankSharpIcon from '@material-ui/icons/CheckBoxOutlineBlankSharp';
import ReactMarkdown from 'react-markdown';
import Loading from "./Loading";
import DataError from "./DataError";
import exportIcon from "../assets/images/export.jpg";
import ErrorFallback from "./ErrorFallback";
import OptionsPopupText from '../docs/OptionsMarkdown.md';
import GeneralInfoPopupText from '../docs/GeneralInfoMarkdown.md';

require('highcharts/modules/exporting')(Highcharts);
require('highcharts/modules/export-data')(Highcharts);
require("highcharts/modules/accessibility")(Highcharts);

function Main() {
  const centerColor = "#00C9FF";
  const dsaColor = "#FBDB68";
  const regionColor = "#E45D2C";
  const nationColor = "#182474";
  const bodyColor = "#212529";
  const tableStripesColor = "#f2f2f2";
  const { REACT_APP_BASE_URL } = process.env;
  const footerText = '"N/A" is printed in cases where N < 5 for a given center/DSA/region.';
  const optionsButtonText = "What do these options mean?";
  const [hasDataError, setHasDataError] = useState(false);
  const [stateIsSelected, setStateIsSelected] = useState(false);
  const [txCenterTableData, setTxCenterTableData] = useState([]);
  const [dsaTableData, setDsaTableData] = useState([]);
  const [regionTableData, setRegionTableData] = useState([]);
  const [nationTableData, setNationTableData] = useState([]);
  const [showDataButtonClicked, setShowDataButtonClicked] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [statesData, setStatesData] = useState(null);
  const [allCenterData, setAllCenterData] = useState(null);
  const [stateCenterData, setStateCenterData] = useState(null);
  const [meldData, setMeldData] = useState(null);
  const [bloodTypeData, setBloodTypeData] = useState(null);
  const [ageData, setAgeData] = useState(null);
  const [exceptionData, setExceptionData] = useState(null);
  const [sliderMarks, setSliderMarks] = useState(null);
  const [selectedState, setSelectedState] = useState("Choose a state");
  const [selectedCenter, setSelectedCenter] = useState("");
  const [selectedMeld, setSelectedMeld] = useState("");
  const [selectedBloodType, setSelectedBloodType] = useState("");
  const [selectedAge, setSelectedAge] = useState("");
  const [selectedException, setSelectedException] = useState("");
  const [sliderValue, setSliderValue] = useState("30");
  const [sliderUnit, setSliderUnit] = useState("Days");
  const [centerCheckboxValue, setCenterCheckboxValue] = useState(false);
  const [dsaCheckboxValue, setDsaCheckboxValue] = useState(false);
  const [regionCheckboxValue, setRegionCheckboxValue] = useState(false);
  const [nationCheckboxValue, setNationCheckboxValue] = useState(false);
  const [series, setSeries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dateInfo, setDateInfo] = useState("");
  const [categories, setCategories] = useState(null);
  const [categoriesN, setCategoriesN] = useState(1);
  const [includeFootnote, setIncludeFootnote] = useState(false);
  const [showOptionsPopup, setShowOptionsPopup] = useState(false);
  const [optionsPopupText, setOptionsPopupText] = useState("");
  const [showGeneralInfoPopup, setShowGeneralInfoPopup] = useState(false);
  const [generalInfoPopupText, setGeneralInfoPopupText] = useState("");
  const [centerNText, setCenterNText] = useState(" (N < 5, results not shown)");
  const [dsaNText, setDsaNText] = useState(" (N < 5, results not shown)");
  const [regionNText, setRegionNText] = useState(" (N < 5, results not shown)");
  const [nationNText, setNationNText] = useState(" (N < 5, results not shown)");
    
  const [chartOptions, setOptions] = useState({
    chart: {
      type: 'column',
      style: {
        fontFamily: "\"Open Sans\", Arial, \"Helvetica Neue\", Helvetica, sans-serif",
        color: regionColor,
      },
      height: 500,
      spacingLeft: 0
    },
    responsive: {
      rules: [{
        condition: {
          maxWidth: 500,
        },
        chartOptions: {
          chart: {
            height: 650
          },
          xAxis: [{
            labels: {
              style: {
                color: bodyColor,
                fontSize: ".8rem",
                textOverflow: "none"
              }
            }
          }],
        }
      }]
    },
    title: {
      text: "Patient Outcomes",
      widthAdjust: -100, // make sure chart tile and export button don't overlap
      align: "left",
      style: {
        fontFamily: "\"montserrat-bold\", Arial, \"Helvetica Neue\", Helvetica, sans-serif",
        color: "#070072"
      },
      margin: 25
    },
    subtitle: {
      text: "",
      align: "left",
      style: {
        fontFamily: "\"Open Sans\", Arial, \"Helvetica Neue\", Helvetica, sans-serif",
        fontWeight: 400,
        fontSize: ".9rem",
        lineHeight: "22px",
        color: "#212529"
      }
    },
    series: Array(categoriesN).fill(0),
    yAxis: [{
      min: 0,
      max: 100,
      title: {
        enabled: false
      },
      labels: {
        format: "{value}%",
        style: {
          color: bodyColor,
          fontSize: ".9rem"
        }
      }
    }],
    xAxis: [{
      labels: {
        style: {
          color: bodyColor,
          fontSize: ".9rem",
          textOverflow: "none"
        }
      }
    },
    ],
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      useHTML: true,
      labelFormatter: function () {
          return '<span white-space="normal" word-wrap="break-word">' + this.name + '</span>';
      },
      itemStyle: {
        color: bodyColor,
        fontSize: ".9rem",
        fontWeight: "normal",
        textOverflow: undefined,
        cursor: 'default'
      },
      itemHoverStyle: {
        color: undefined
      }
    },
    tooltip: {
      formatter: function () {
        return '<b>' + this.series.userOptions.tooltipDisplay + '</b><br/>' +
          this.point.y.toFixed(1) + '%';
      },
      style: {
        fontSize: "16px"
      }
    },
    
    navigation: {
      buttonOptions: {
        theme: {
          states: {
            hover: {
              fill: 'none',
              stroke: 'none'
            },
            select: {
              stroke: 'none',
              fill: 'none'
            }
          }
        }
      }
    }
  });
  const defaultMarks = [
    {
      value: 0,
      label: "0"
    },
    {
      value: 30,
      label: "30"
    },
    {
      value: 60,
      label: "60"
    },
    {
      value: 90,
      label: "90"
    },
    {
      value: 180,
      label: "180"
    },
    {
      value: 365,
      label: "365"
    }
  ];

  const handleErrors = (response) => {
    if (!response.ok) {
      console.log("Unable to get data");
      setHasDataError(true);
      setIsLoading(false);
    }
    return response;
  }

  const getVariables = async () => {
    let response = await fetch(`${process.env.REACT_APP_API_HOST}/variables`)
    .then(handleErrors)
    .catch(error => console.log(error));
    await response.json().then(result => {
      let levelsData = {};
      if (result.levels) {
        for (let level of result.levels) {
          let key = level.name;
          let value = level.group;
          if (!levelsData.hasOwnProperty(key)) {
            levelsData[key] = [value];
          } else {
            levelsData[key].push(value);
          }
        }
      }
      // levelsData is object with group as key and name as value(s)
      // keys: blood_type, age_range, meld_range, exception_status, sample_period
      // i.e. levelsData["ABO_Group"] = [A, B, AB, O]

      let transplantCenters = result.transplantCenters;

      let states = new Set();
      if (transplantCenters) {
        for (let entry of transplantCenters) {
          states.add(entry.state);
        }
      }
      let uniqueStates = [...states].sort();

      let marks = [];
      if (levelsData["Time_Frame"]) {
        for (let entry of levelsData["Time_Frame"]) {
          let valueString = entry.match(/\d+/);
          marks.push(
            {
              value: Number(valueString),
              label: valueString,
            }
          );
        }  

        setSliderUnit(levelsData["Time_Frame"][0].match(/[a-zA-Z]+/));
      }

      if (!marks.find(x => x.value === 0)) {
        marks.unshift(
          {
            value: 0,
            label: "0",
            enabled: false
          }
        );
      }

      if (uniqueStates && transplantCenters && levelsData && marks && result.lastUpdateMessage) {
        setSelectedMeld(levelsData["MELD_Group"][Math.floor(levelsData["MELD_Group"].length / 2)]);
        setSelectedBloodType(levelsData["ABO_Group"][0]);
        setSelectedAge(levelsData["Age_Group"][0]);
        setSelectedException(levelsData["Exception_Status"][0]);
  
        setStatesData(uniqueStates);
        setAllCenterData(transplantCenters);
        setMeldData(levelsData["MELD_Group"]);
        setBloodTypeData(levelsData["ABO_Group"]);
        setAgeData(levelsData["Age_Group"]);
        setExceptionData(levelsData["Exception_Status"]);
        setSliderMarks(marks);
        setCategories(levelsData["Sample_WL_Outcome"]);
        setCategoriesN(levelsData["Sample_WL_Outcome"].length);
		
		    setDateInfo(result.lastUpdateMessage);
      }
      else setHasDataError(true);
      
    });
  }

  const getChartData = async () => {
    setIsLoading(true);
    let body = {
      AgeRange: selectedAge,
      BloodType: selectedBloodType,
      Center: selectedCenter,
      ExceptionStatus: selectedException,
      MELDRange: selectedMeld,
      SamplePeriod: sliderValue.toString() + " " + sliderUnit
    };
    let response = await fetch(`${process.env.REACT_APP_API_HOST}/outcomes`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    })
    .then(handleErrors)
    .catch(error => console.log(error));
    await response.json().then(result => {
      setChartData(result);
      setIsLoading(false);
      //chartData example: 
      //geography: "Region"
      //observationCount: 11
      //percent: 1.0119595216191353
      //sampleWLOutcome: "Condition Improved"
    });
  }

  const getPopupText = async () => {
    await fetch(OptionsPopupText).then((response) => response.text()).then((text) => {
      setOptionsPopupText(text);
    });
    await fetch(GeneralInfoPopupText).then((response) => response.text()).then((text) => {
      setGeneralInfoPopupText(text);
    });
  }

  useEffect(() => {
    getPopupText();
  }, [])

  useEffect(() => {
    getVariables();
  }, []);// eslint-disable-line react-hooks/exhaustive-deps
  
  useEffect(() => {
    getChartData();
  }, [selectedAge, selectedBloodType, selectedCenter, // eslint-disable-line react-hooks/exhaustive-deps
  selectedState, selectedException, selectedMeld, sliderValue])

  useEffect(() => {
    createSeries();
  }, [chartData]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    defineChart();
  }, [series, centerCheckboxValue, dsaCheckboxValue, // eslint-disable-line react-hooks/exhaustive-deps
  regionCheckboxValue, nationCheckboxValue])

  const createSeries = () => {
    let txCenterPercent = Array(categoriesN).fill(0);
    let dsaPercent = Array(categoriesN).fill(0);
    let regionPercent = Array(categoriesN).fill(0);
    let nationPercent = Array(categoriesN).fill(0);

    let txCenterN = 0;
    let dsaN = 0;
    let regionN = 0;
    let nationN = 0;

	  let i = 0;

    if (categories) {
        for (let outcome of categories) {
            for (let data of chartData) {
                if (data.sampleWLOutcome === outcome) {
                    if (data.geography === "Tx-Center") {
                        txCenterPercent[i] = data.percent;
                        if (!txCenterN) {
                            txCenterN = Number(data.groupTotal);
                        }
                    } else if (data.geography === "DSA") {
                        dsaPercent[i] = data.percent;
                        if (!dsaN) {
                            dsaN = Number(data.groupTotal);
                        }
                    } else if (data.geography === "Region") {
                        regionPercent[i] = data.percent;
                        if (!regionN) {
                            regionN = Number(data.groupTotal);
                        }
                    } else {
                        nationPercent[i] = data.percent;
                        if (!nationN) {
                            nationN = Number(data.groupTotal);
                        }
                    }
                }
            }
            i++;
        }
    }

    let footnoteShouldBeIncluded = false;

    if (!txCenterPercent.some(item => item !== 0)) { // the array is all zeros
      setTxCenterTableData(Array(categoriesN).fill("N/A"));
      footnoteShouldBeIncluded = true;
    }
    else {
      setTxCenterTableData(txCenterPercent.map(item => {
        return item.toFixed(1) + "%";
      }));
    }
    if (!dsaPercent.some(item => item !== 0)) { // the array is all zeros
      setDsaTableData(Array(categoriesN).fill("N/A"));
      footnoteShouldBeIncluded = true;
    }
    else {
      setDsaTableData(dsaPercent.map(item => {
        return item.toFixed(1) + "%";
      }));
    }
    if (!regionPercent.some(item => item !== 0)) { // the array is all zeros
      setRegionTableData(Array(categoriesN).fill("N/A"));
      footnoteShouldBeIncluded = true;
    }
    else {
      setRegionTableData(regionPercent.map(item => {
        return item.toFixed(1) + "%";
      }));
    }
    if (!nationPercent.some(item => item !== 0)) { // the array is all zeros
      setNationTableData(Array(categoriesN).fill("N/A"));
      footnoteShouldBeIncluded = true;
    }
    else {
      setNationTableData(nationPercent.map(item => {
        return item.toFixed(1) + "%";
      }));
    }

    setIncludeFootnote(footnoteShouldBeIncluded);

    let centerNText = txCenterN ? " (N = " + txCenterN + ")" : " (N < 5, results not shown)";
    let dsaNText = dsaN ? " (N = " + dsaN + ")" : " (N < 5, results not shown)";
    let regionNText = regionN ? " (N = " + regionN + ")" : " (N < 5, results not shown)";
    let nationNText = nationN ? " (N = " + nationN + ")" : " (N < 5, results not shown)";

    setCenterNText(centerNText);
    setDsaNText(dsaNText);
    setRegionNText(regionNText);
    setNationNText(nationNText);

    let centerName = selectedCenter ? selectedCenter : "Center";
    let dsaName = "DSA";
    let regionName = "OPTN Region";
    let nationName = "U.S.";

    setSeries(
      [
        {
          name: centerName + " " + centerNText,
          tooltipDisplay: centerName,
          data: txCenterPercent,
          color: centerColor,
          visible: centerCheckboxValue,
          events: {
        		legendItemClick: function(x) {
            	x.preventDefault()
            }
          }
        },
        {
          name: dsaName + " " + dsaNText,
          tooltipDisplay: dsaName,
          data: dsaPercent,
          color: dsaColor,
          visible: dsaCheckboxValue,
          events: {
        		legendItemClick: function(x) {
            	x.preventDefault()
            }
          }
        },
        {
          name: regionName + " " + regionNText,
          tooltipDisplay: regionName,
          data: regionPercent,
          color: regionColor,
          visible: regionCheckboxValue,
          events: {
        		legendItemClick: function(x) {
            	x.preventDefault()
            }
          }
        },
        {
          name: nationName + " " + nationNText,
          tooltipDisplay: nationName,
          data: nationPercent,
          color: nationColor,
          visible: nationCheckboxValue,
          events: {
        		legendItemClick: function(x) {
            	x.preventDefault()
            }
          }
        }
      ]
    );
  }

  const defineChart = () => {
    let fullDate = (new Date()).toLocaleDateString("en-US");
    let fileName = "patient-outcomes-at-" + sliderValue + "-" + String(sliderUnit).toLowerCase();

    let subtitleTextBase = `Center: ${selectedCenter}; MELD score range: ${selectedMeld}; Blood type: ${selectedBloodType}; Age range: ${selectedAge}; Exception status: ${selectedException}.`
    let subtitleTextDefault = stateIsSelected ? subtitleTextBase : "";
    let subtitleTextExport = stateIsSelected ? subtitleTextBase + ` (Accessed on ${fullDate}.)` : "";

    let newOptions = {
      ...chartOptions,
      chart: {
        events: {
          beforePrint: function () {
            this.update({subtitle: {text: subtitleTextExport}})
          },
          afterPrint: function () {
            this.update({subtitle: {text: subtitleTextDefault}})
          }
        }
      },
      title: {
        text: `Patient Outcomes at ${sliderValue} ${sliderUnit}`
      },
      subtitle: {
        text: subtitleTextDefault
      },
      series: series,
      navigation: {
        buttonOptions: {
          enabled: true
        }
      },
      xAxis: [{
        categories: categories,
      }],
      exporting: {
        sourceHeight: 600,
        chartOptions: {
          subtitle: {
            text: subtitleTextExport
          }, 
        },
        filename: fileName,
        menuItemDefinitions: {
          'customDownloadCSV': {
            onclick: function() {
              exportTableToCSV(fileName + ".csv");
            },
            text: 'Download CSV'
          }
        },
        buttons: {
          contextButton: {
            symbol: `url(${exportIcon})`,
            symbolX: -50,
            menuItems: ['printChart', 'downloadPNG', 'downloadSVG', 'downloadPDF', 'customDownloadCSV']
          }
        }
      },
    };

    setOptions(newOptions);
  }

  const downloadCSV = (csv, filename) => {
    var csvFile;
    var downloadLink;

    // CSV file
    csvFile = new Blob([csv], {type: "text/csv"});

    // Download link
    downloadLink = document.createElement("a");

    // File name
    downloadLink.download = filename;

    // Create a link to the file
    downloadLink.href = window.URL.createObjectURL(csvFile);

    // Hide download link
    downloadLink.style.display = "none";

    // Add the link to DOM
    document.body.appendChild(downloadLink);

    // Click download link
    downloadLink.click();
  }

  const exportTableToCSV = (filename) => {
    let fullDate = (new Date()).toLocaleDateString("en-US");
    let csv = [];
    let rows = document.querySelectorAll("table tr");
    
    for (let i = 0; i < rows.length; i++) {
      if (isHidden(rows[i])) continue;
      let skip = false;
      let row = [], cols = rows[i].querySelectorAll("td, th");
        
      for (let j = 0; j < cols.length; j++) {
        if (cols[j].innerText === footerText) {
          skip = true;
          continue;
        }
        row.push('"' + cols[j].innerText + '"');
      }
      
      if (skip) continue;
      csv.push(row.join(","));        
    }

    if (includeFootnote) csv.push(footerText);
    csv.push(`(Accessed on ${fullDate})`)

    downloadCSV(csv.join("\n"), filename);
  }

  const isHidden = (element) => {
    let style = window.getComputedStyle(element);
    return ((style.display === 'none') || (style.visibility === 'hidden'))
  }

  const handleStateSelect = (selectedState) => {
    setSelectedState(selectedState);
    let centersForThisState = allCenterData.filter(center => center.state === selectedState);
    setStateCenterData(centersForThisState);
    setSelectedCenter(centersForThisState[0].name)
    if (!stateIsSelected) setCenterCheckboxValue(true);
    setStateIsSelected(true);
  }

  const handleCenterSelect = (selectedCenter) => {
    setSelectedCenter(selectedCenter);
  }

  const handleMeldSelect = (selectedMeld) => {
    setSelectedMeld(selectedMeld);
  }

  const handleAgeSelect = (selectedAge) => {
    setSelectedAge(selectedAge);
  }

  const handleBloodTypeSelect = (selectedBloodType) => {
    setSelectedBloodType(selectedBloodType);
  }

  const handleExceptionSelect = (selectedException) => {
    setSelectedException(selectedException);
  }

  const handleCenterChange = (centerIsChecked) => {
    setCenterCheckboxValue(centerIsChecked);
    series[0].visible = centerIsChecked;
  }

  const handleDsaChange = (dsaIsChecked) => {
    setDsaCheckboxValue(dsaIsChecked);
    series[1].visible = dsaIsChecked;
  }

  const handleRegionChange = (regionIsChecked) => {
    setRegionCheckboxValue(regionIsChecked);
    series[2].visible = regionIsChecked;
  }

  const handleNationChange = (nationIsChecked) => {
    setNationCheckboxValue(nationIsChecked);
    series[3].visible = nationIsChecked;
  }

  const handleSliderChange = (value) => {
    if (value) {
      setSliderValue(value);
    }
  }

  const handleGeneralInfoPopupShow = () => {
    setShowGeneralInfoPopup(true);
  }

  const handleGeneralInfoPopupClose = () => {
    setShowGeneralInfoPopup(false);
  }

  const handleOptionsPopupShow = () => {
    setShowOptionsPopup(true);
  }

  const handleOptionsPopupClose = () => {
    setShowOptionsPopup(false);
  }

  const handleMethodologyButtonClick = () => {
    const url = [REACT_APP_BASE_URL, "tools", "liver-waiting-list-calculator", "waitlist-calculation-documentation"];
    window.open(url.join('/'));
  }

  const handleShowDataButtonClick = () => {
    setShowDataButtonClicked(!showDataButtonClicked);
  }

  const handlePrintButtonClick = () => {
    window.print();
  }

  const evenNumberOfTrues = (array) => {
    let trueValues = 0;
    for (let i = 0; i < array.length; i++) {
      if (array[i]) trueValues++;
    }
    return trueValues % 2 === 0;
  }

  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <Row>
        <Col>
          <Row>
            <Col>
              <div className="bold-instructions heading">
                Liver Waiting List Outcomes Calculator
              </div>
              <div className="instructions">
                What were the outcomes on the liver transplant waiting list?
                This tool shows actual patient experiences over the past two years.
                See more about these data {" "}
                <Button className="link" onClick={handleGeneralInfoPopupShow} variant="link">here</Button>.
              </div>
            </Col>
          </Row>

          <Modal show={showGeneralInfoPopup} onHide={handleGeneralInfoPopupClose} dialogClassName="modal-container">
            <Modal.Header closeButton>
              <Modal.Title className="bold-instructions">What are these data?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div>
                <ReactMarkdown className="instructions" children={generalInfoPopupText} />
              </div>
            </Modal.Body>
          </Modal>

          <Row>
            <Col md="6" sm="12">
              <Row>
                <Col xs="12">
                  <div key="required-div" className="bold-instructions">
                    Required
                  </div>
                  <div key="state-div" className="instructions">
                    Choose the state the center is located in:
                    <DropdownButton
                      id="dropdown-menu"
                      align="left"
                      variant="outline-secondary"
                      title={selectedState ? selectedState : "Loading states..."}
                      onSelect={handleStateSelect}>
                      {statesData && statesData.map((state) => {
                        return (
                          <Dropdown.Item id="dropdown-item" key={state} eventKey={state}>{state}</Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </div>
                </Col>
                <Col xs="12">
                  <div key="center-div" className="instructions">
                    Choose the liver transplant center:
                    <DropdownButton
                      id="dropdown-menu"
                      align="left"
                      variant="outline-secondary"
                      title={selectedCenter ? selectedCenter : ""}
                      disabled={!stateIsSelected}
                      onSelect={handleCenterSelect}>
                      {stateCenterData && stateCenterData.map((center) => {
                        return (
                          <Dropdown.Item id="dropdown-item" key={center.name} eventKey={center.name}>{center.name}</Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </div>
                </Col>
                <Col xs="12">
                  <div key="meld-div" className="instructions">
                    Choose a MELD score range:
                    <DropdownButton
                      id="dropdown-menu"
                      align="left"
                      variant="outline-secondary"
                      title={selectedMeld ? selectedMeld : "Loading ranges..."}
                      disabled={!stateIsSelected}
                      onSelect={handleMeldSelect}>
                      {meldData && meldData.map((meld) => {
                        return (
                          <Dropdown.Item id="dropdown-item" key={meld} eventKey={meld}>{meld}</Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </div>
                </Col>
              </Row>
            </Col>
            <Col md="6" sm="12">
              <Row>
                <Col xs="12">
                  <div key="optional-div" className="bold-instructions">
                    Optional
                  </div>
                  <div key="bloodtype-div" className="instructions">
                    Choose your blood type:
                    <DropdownButton
                      id="dropdown-menu"
                      align="left"
                      variant="outline-secondary"
                      title={selectedBloodType ? selectedBloodType : "Loading types..."}
                      disabled={!stateIsSelected}
                      onSelect={handleBloodTypeSelect}>
                      {bloodTypeData && bloodTypeData.map((bloodType) => {
                        return (
                          <Dropdown.Item id="dropdown-item" key={bloodType} eventKey={bloodType}>{bloodType}</Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </div>
                </Col>
                <Col xs="12">
                  <div key="age-div" className="instructions">
                    Choose your age:
                    <DropdownButton
                      id="dropdown-menu"
                      align="left"
                      variant="outline-secondary"
                      title={selectedAge ? selectedAge : "Loading ranges..."}
                      disabled={!stateIsSelected}
                      onSelect={handleAgeSelect}>
                      {ageData && ageData.map((age) => {
                        return (
                          <Dropdown.Item id="dropdown-item" key={age} eventKey={age}>{age}</Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </div>
                </Col>
                <Col xs="12">
                  <div key="exception-div" className="instructions" id="instructions">
                    Include / exclude patients with MELD exception points:
                    <DropdownButton
                      id="dropdown-menu"
                      align="left"
                      variant="outline-secondary"
                      title={selectedException ? selectedException : "Loading options..."}
                      disabled={!stateIsSelected}
                      onSelect={handleExceptionSelect}>
                      {exceptionData && exceptionData.map((option) => {
                        return (
                          <Dropdown.Item id="dropdown-item" key={option} eventKey={option}>{option}</Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="flex-column-reverse flex-md-row">
            <Col md="6" sm="12">
              <div key="checkboxes-div" className="bold-instructions">
                Select Result Options to Display
              </div>
              <FormGroup row key="row" className="instructions">
                <FormControlLabel key="form-center"
                  control={
                    <Checkbox key="checkbox-center"
                      checked={centerCheckboxValue}
                      onChange={(_event, isChecked) => handleCenterChange(isChecked)}
                      style={{
                        color: centerColor
                      }}
                      checkedIcon={<CheckBoxSharpIcon />}
                      icon={<CheckBoxOutlineBlankSharpIcon />}
                      disabled={!stateIsSelected}
                    />
                  }
                  label="Center"
                />

                <FormControlLabel key="form-dsa"
                  control={
                    <Checkbox key="checkbox-dsa"
                      checked={dsaCheckboxValue}
                      onChange={(_event, isChecked) => handleDsaChange(isChecked)}
                      style={{
                        color: dsaColor
                      }}
                      checkedIcon={<CheckBoxSharpIcon />}
                      icon={<CheckBoxOutlineBlankSharpIcon />}
                      disabled={!stateIsSelected}
                    />
                  }
                  label="DSA"
                />

                <FormControlLabel key="form-region"
                  control={
                    <Checkbox key="checkbox-region"
                      checked={regionCheckboxValue}
                      onChange={(_event, isChecked) => handleRegionChange(isChecked)}
                      style={{
                        color: regionColor
                      }}
                      checkedIcon={<CheckBoxSharpIcon />}
                      icon={<CheckBoxOutlineBlankSharpIcon />}
                      disabled={!stateIsSelected}
                    />
                  }
                  label="OPTN Region"
                />

                <FormControlLabel key="form-nation"
                  control={
                    <Checkbox key="checkbox-nation"
                      checked={nationCheckboxValue}
                      onChange={(_event, isChecked) => handleNationChange(isChecked)}
                      style={{
                        color: nationColor
                      }}
                      checkedIcon={<CheckBoxSharpIcon />}
                      icon={<CheckBoxOutlineBlankSharpIcon />}
                      disabled={!stateIsSelected}
                    />
                  }
                  label="Nation"
                />
              </FormGroup>
            </Col>
            <Col md="6" sm="12">
              <div key="slider-div" className="bold-instructions">
                How Many {sliderUnit} of Follow-Up Are You Interested In?
              </div>
              <div key="slider" >
                <Slider key="slider"
                  className="slider"
                  onChangeCommitted={(event, value) => handleSliderChange(value)}
                  defaultValue={sliderMarks ? sliderMarks[1].value : defaultMarks[1].value}
                  step={null}
                  marks={sliderMarks ? sliderMarks : defaultMarks}
                  max={sliderMarks ? sliderMarks[sliderMarks.length - 1].value * 1.01 : defaultMarks[defaultMarks.length - 1].value * 1.01}
                  min={sliderMarks ? sliderMarks[sliderMarks.length - 1].value * -0.007 : defaultMarks[defaultMarks.length - 1].value * -0.007}
                  valueLabelDisplay="off"
                  value={Number(sliderValue)}
                  disabled={!stateIsSelected}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <Sentry.ErrorBoundary fallback={ErrorFallback}>
                <div key="div-chart" id="chart">
                  <Loading loading={isLoading} />
                  <DataError error={hasDataError} />
                  <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                </div>
              </Sentry.ErrorBoundary>
            </Col>
          </Row>

          <Row>
            <Col>
              <Button className="white-btn" onClick={handleOptionsPopupShow}>
                {optionsButtonText}
              </Button>{' '}
              <Button className="white-btn" onClick={handleMethodologyButtonClick}>
                Methodology
              </Button>{' '}
              <Button className="blue-btn show-data" onClick={handleShowDataButtonClick} disabled={!stateIsSelected}>
                {!showDataButtonClicked ? "Show Data" : "Hide Data"}
              </Button>{' '}
              <Button className="blue-btn" onClick={handlePrintButtonClick}>
                Print
              </Button>{' '}
            </Col>
          </Row>

          <Modal show={showOptionsPopup} 
            onHide={handleOptionsPopupClose} 
            dialogClassName="modal-container"
            scrollable={true}>
            <Modal.Header closeButton>
              <Modal.Title className="bold-instructions">{optionsButtonText}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div>
                <ReactMarkdown className="instructions" children={optionsPopupText} />
              </div>
            </Modal.Body>
          </Modal>

          <Collapse in={showDataButtonClicked} className="table"> 
            <Row>
              <Col>
              <Table bordered>
                <thead>
                  <tr key="header">
                    <th className="invisible"></th>
                    {categories && categories.map(head => <th key={head}>{head}</th>)}
                  </tr>
                </thead>
                <Collapse in={includeFootnote}>
                  <tfoot>
                    <tr style={evenNumberOfTrues([centerCheckboxValue, dsaCheckboxValue, regionCheckboxValue, nationCheckboxValue]) ? {backgroundColor: tableStripesColor} : {backgroundColor: 'white'}}>
                      <td colSpan={categoriesN + 1} className="instructions">{footerText}</td>
                    </tr>
                  </tfoot>
                </Collapse>
                <tbody>
                  <Collapse in={centerCheckboxValue}>
                    <tr key="center" style={{backgroundColor: tableStripesColor}}>
                      <td className="leftHeader">{selectedCenter + " " + centerNText}</td>
                      {txCenterTableData.map((entry, index) => <td key={"center-" + entry + "-" + index}>{entry}</td>)}
                    </tr>
                  </Collapse>
                  <Collapse in={dsaCheckboxValue}>
                    <tr key="DSA" style={centerCheckboxValue ? {backgroundColor: 'white'} : {backgroundColor: tableStripesColor}}>
                      <td className="leftHeader">{"DSA " + dsaNText}</td>
                      {dsaTableData.map((entry, index) => <td key={"center-" + entry + "-" + index}>{entry}</td>)}
                    </tr>
                  </Collapse>
                  <Collapse in={regionCheckboxValue}>
                    <tr key="region" style={evenNumberOfTrues([centerCheckboxValue, dsaCheckboxValue]) ? {backgroundColor: tableStripesColor} : {backgroundColor: 'white'}}>
                      <td className="leftHeader">{"Region " + regionNText}</td>
                      {regionTableData.map((entry, index) => <td key={"center-" + entry + "-" + index}>{entry}</td>)}
                    </tr>
                  </Collapse>
                  <Collapse in={nationCheckboxValue}>
                    <tr key="nation" style={evenNumberOfTrues([centerCheckboxValue, dsaCheckboxValue, regionCheckboxValue]) ? {backgroundColor: tableStripesColor} : {backgroundColor: 'white'}}>
                      <td className="leftHeader">{"Nation " + nationNText}</td>
                      {nationTableData.map((entry, index) => <td key={"center-" + entry + "-" + index}>{entry}</td>)}
                    </tr>
                  </Collapse>
                </tbody>
              </Table>
              </Col>
            </Row>
          </Collapse>
          
          <Row>
            <Col>
            <div key="update-message" className="instructions">
              {dateInfo}
            </div>
            </Col>
          </Row>

        </Col>
      </Row>
    </Sentry.ErrorBoundary>
  )
}

export default Main;
